import React from 'react';
import { useNavigate } from 'react-router-dom';
import { reducedModeEnabled } from '../utils/reducedMode';
import { AdTypeKey } from '../utils/adSystem';

interface StaticAdProps {
  type: AdTypeKey;
  onClose: () => void;
}

export const StaticAd: React.FC<StaticAdProps> = ({ type, onClose }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate(reducedModeEnabled ? '/feedback?from=/party-ad' : '/subscription');
  };

  const getAdContent = () => {
    switch (type) {
      case 'house':
        return {
          title: reducedModeEnabled ? '🎤 Help Shape QrOke' : '🎤 Upgrade to QrOke Pro!',
          subtitle: reducedModeEnabled ? 'Free hosting stays focused on the essentials. Tell us what would make your setup better.' : 'Unlimited Karaoke Fun Awaits',
          content: (
            reducedModeEnabled ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-stone-800 to-slate-700 text-white p-6 rounded-lg">
                  <h3 className="text-2xl font-bold mb-4">What would unblock your event?</h3>
                  <ul className="space-y-2 text-lg">
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🎛️</span>
                      <span>Missing host controls or setup tools</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🎨</span>
                      <span>Branding or venue-facing surfaces</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">💳</span>
                      <span>Billing or paid session paths</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🔐</span>
                      <span>OAuth or advanced integration requests</span>
                    </li>
                  </ul>
                </div>
                <div className="text-center bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-900 font-semibold">
                    Feedback goes straight to the admin inbox from inside the app.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg">
                  <h3 className="text-2xl font-bold mb-4">Why Go Pro?</h3>
                  <ul className="space-y-2 text-lg">
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">⏰</span>
                      <span>Unlimited party time (no 1-hour limit!)</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🎵</span>
                      <span>Unlimited song queue</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🎨</span>
                      <span>Custom venue branding</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-2xl mr-3">🚫</span>
                      <span>No ads interruptions</span>
                    </li>
                  </ul>
                </div>
                <div className="text-center">
                  <p className="text-xl text-gray-700 mb-4">
                    Join thousands of happy karaoke hosts!
                  </p>
                  <div className="bg-green-100 p-4 rounded-lg">
                    <p className="text-green-800 font-semibold">
                      Special Offer: Get your first month for just $9.99!
                    </p>
                  </div>
                </div>
              </div>
            )
          ),
          buttonText: reducedModeEnabled ? 'Give Feedback / Request Feature' : 'Upgrade Now - $9.99/month',
          buttonColor: reducedModeEnabled ? 'bg-gradient-to-r from-stone-700 to-slate-800 hover:from-stone-800 hover:to-slate-900' : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
        };

      case 'lean':
        return {
          title: reducedModeEnabled ? '🌟 Built For Fast Hosting' : '🌟 Discover QrOke Features',
          subtitle: reducedModeEnabled ? 'Free hosting tuned for QR join, simple search, and a clean queue flow' : 'Professional Karaoke Made Easy',
          content: (
            reducedModeEnabled ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-bold text-green-800 mb-2">📱 QR Join</h4>
                    <p className="text-green-700">Guests join quickly without installing anything.</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">🎵 Lightweight Search</h4>
                    <p className="text-blue-700">Browser search when configured, local catalog when it is not.</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-bold text-purple-800 mb-2">⚡ Live Queue</h4>
                    <p className="text-purple-700">A clear next-up flow with simple automatic handoff between songs.</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-bold text-orange-800 mb-2">🎭 Playback Screen</h4>
                    <p className="text-orange-700">Keep the QR code, splash, and active track visible on the main display.</p>
                  </div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 font-medium">
                    QrOke favors reliable hosting over feature sprawl.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">🎤 Host Controls</h4>
                    <p className="text-blue-700">Professional audio routing, external displays, and queue management</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-bold text-green-800 mb-2">📱 QR Code Magic</h4>
                    <p className="text-green-700">Guests scan to join instantly - no app downloads needed!</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-bold text-purple-800 mb-2">🎵 YouTube Integration</h4>
                    <p className="text-purple-700">Access millions of karaoke tracks with synchronized lyrics</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="font-bold text-orange-800 mb-2">🎨 Custom Branding</h4>
                    <p className="text-orange-700">Make it yours with venue logos, colors, and themes</p>
                  </div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 font-medium">
                    Perfect for bars, restaurants, private parties, and events!
                  </p>
                </div>
              </div>
            )
          ),
          buttonText: reducedModeEnabled ? 'Open Lean Guide' : 'Try All Features - Start Free!',
          buttonColor: 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600'
        };

      case 'sponsor':
        return {
          title: reducedModeEnabled ? '❤️ Keep Free Hosting Sustainable' : '❤️ What Our Users Say',
          subtitle: reducedModeEnabled ? 'House promos and sponsor spots can support free karaoke nights without closing the app behind billing.' : 'Real Stories from Happy Hosts',
          content: (
            reducedModeEnabled ? (
              <div className="space-y-4">
                <div className="bg-white border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
                  <p className="text-gray-700 mb-2">
                    This break can be used for your own house promo, a local sponsor message, or a simple reminder that free hosting is supported by short promotional spots.
                  </p>
                </div>

                <div className="bg-white border-l-4 border-green-500 p-4 rounded-r-lg shadow-sm">
                  <p className="text-gray-700 mb-2">
                    If you run a venue, bar, or recurring event, these slots can be repurposed for drink specials, upcoming nights, or sponsor inventory instead of generic upsell copy.
                  </p>
                </div>

                <div className="text-center bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-800 font-semibold">
                    Want to sponsor QrOke or use branded house promos? Get in touch.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
                  <p className="text-gray-700 italic mb-2">
                    "QrOke transformed our bar's karaoke nights! The QR code system is genius - 
                    customers love how easy it is to add songs. Our revenue increased 40% on karaoke nights!"
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      M
                    </div>
                    <div className="ml-3">
                      <p className="font-semibold text-gray-800">Mike Johnson</p>
                      <p className="text-sm text-gray-600">Owner, The Singing Pub</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-l-4 border-green-500 p-4 rounded-r-lg shadow-sm">
                  <p className="text-gray-700 italic mb-2">
                    "Used QrOke for my wedding reception - it was a huge hit! 
                    The custom branding made it feel professional, and everyone had a blast."
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                      S
                    </div>
                    <div className="ml-3">
                      <p className="font-semibold text-gray-800">Sarah Chen</p>
                      <p className="text-sm text-gray-600">Happy Bride</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-l-4 border-purple-500 p-4 rounded-r-lg shadow-sm">
                  <p className="text-gray-700 italic mb-2">
                    "As an event DJ, QrOke is my secret weapon. The host controls 
                    and audio routing features are exactly what professionals need!"
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                      D
                    </div>
                    <div className="ml-3">
                      <p className="font-semibold text-gray-800">DJ Rodriguez</p>
                      <p className="text-sm text-gray-600">Professional DJ</p>
                    </div>
                  </div>
                </div>

                <div className="text-center bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-800 font-semibold">
                    Join 10,000+ happy karaoke hosts worldwide!
                  </p>
                </div>
              </div>
            )
          ),
          buttonText: reducedModeEnabled ? 'Contact About Sponsor Spots' : 'Start Your Success Story!',
          buttonColor: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
        };

      default:
        return getAdContent();
    }
  };

  const adData = getAdContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-800">{adData.title}</h2>
              <p className="text-xl text-gray-600 mt-1">{adData.subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            {adData.content}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleUpgrade}
              className={`px-8 py-3 text-white font-bold rounded-lg text-lg transition-all duration-200 ${adData.buttonColor}`}
            >
              {adData.buttonText}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Maybe Later
            </button>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              {reducedModeEnabled ? 'QrOke free hosting - supported by short house promos and sponsor inventory' : 'QrOke - Making Karaoke Magical Since 2024'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};